import {AfterViewChecked, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import {required} from '../../../../../../../../../shared/utils';
import { moveItemInArray, CdkDropList, CdkDrag, CdkDragPlaceholder, CdkDragHandle } from '@angular/cdk/drag-drop';
import map from 'lodash/map';
import {EditPermissions} from '../../../../../../../../../shared/models';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { InputValidatorsComponent } from '../../../../templates/input-validators/input-validators.component';
import { CMSComponent } from '../../../../../../../../../shared/components/cms/cms.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { SelectMediaComponent } from '../../../../../../select-media/select-media.component';
import { MatTooltip } from '@angular/material/tooltip';

interface SelectionPage {
  pageType: string;
  order?: number;
  pageHeader: string;
  pageSubHeader: string;
  multipleSelection: boolean;
  options?: SelectionPageOption[];
}

interface SelectionPageOption {
      text: string;
      value: string;
      imageRef: string;
      order?: number;
      identifier?: string;
}

@Component({
    selector: 'selection-page',
    templateUrl: './selection-page.component.html',
    styleUrls: ['../styles.scss', './selection-page.component.scss'],
    imports: [NgTemplateOutlet, NgClass, ReactiveFormsModule, InputValidatorsComponent, CMSComponent, MatSlideToggle, CdkDropList, CdkDrag, CdkDragPlaceholder, SelectMediaComponent, CdkDragHandle, MatTooltip]
})
export class SelectionPageComponent implements OnInit, OnDestroy, AfterViewChecked {
  private activateScroll = false;
  constructor() {}

  @Input() pageData: any;
  @Input() canEdit: EditPermissions;
  @Output() setPageData = new EventEmitter();

  moreInfoOpened: Boolean = false;

  selectPageForm: UntypedFormGroup;
  selectPageFormOptions: UntypedFormArray;
  updateImage: boolean = true;

  ngOnInit() {
    this.selectPageForm = this.getSelectPageTemplate();
    this.selectPageFormOptions = this.getOptionsTemplate();

    const { page, newPage } = this.pageData;
    this.selectPageForm.patchValue(page);
    if (page.options && page.options.length === 0) {
      this.addOption();
    }
    if (page.options && page.options.length > 0) {
      const options = this.pageData.page.options;
      for (const option in options) {
        this.addOption();
      }
      this.selectPageFormOptions.patchValue(options);
    }

    if (newPage === false) {
      this.selectPageForm.markAllAsTouched();
      this.selectPageFormOptions.markAllAsTouched();
    }

    this.setPageDataHandler();
    this.listenForFormChanges();
  }

  ngOnDestroy() {
    this.setPageDataHandler();
  }

  listenForFormChanges() {
    this.selectPageForm.valueChanges.subscribe(values => {
      this.setPageDataHandler();
    });
    this.selectPageFormOptions.valueChanges.subscribe(values => {
      this.setPageDataHandler();
    });
  }

  ngAfterViewChecked(): void {
    this.scrollTo();
  }

  scrollTo() {
    if (this.activateScroll) {
      const elementID = `#option-${this.selectPageFormOptions.length - 1}`;
      document.querySelector(elementID).scrollIntoView({behavior: 'smooth'});
      this.activateScroll = false;
    }
  }

  getSelectPageTemplate() {
    return new UntypedFormGroup({
      pageHeader: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }),
      pageSubHeader: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }, [required('Subheader')]),
      multipleSelection: new UntypedFormControl({
        value: false,
        disabled: !this.canEdit
      })
    });
  }

  getOptionsTemplate() {
    return new UntypedFormArray([]);
  }

  getOptionTemplate() {
    return new UntypedFormGroup({
      text: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }, [required('Title')]),
      value: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }, [required('Value')]),
      imageRef: new UntypedFormControl({
        value: '',
        disabled: !this.canEdit
      }),
      identifier: new UntypedFormControl('')
    });
  }
  openMoreInfo() {
    this.moreInfoOpened = true;
  }

  closeMoreInfo() {
    this.moreInfoOpened = false;
  }

  setImage(event, form) {
    form.get('imageRef').setValue(event);
  }

  addOption() {
    this.selectPageFormOptions.push(this.getOptionTemplate());
    this.activateScroll = true;
  }

  removeOption(index) {
    if (this.selectPageFormOptions.controls.length > 1) {
      const options = this.selectPageFormOptions as UntypedFormArray;
      options.removeAt(index);
    }
  }

  dropOption(event) {
    this.updateImage = false;
    const currentOptionsList = this.selectPageFormOptions.getRawValue();
    moveItemInArray(currentOptionsList, event.previousIndex, event.currentIndex);
    this.selectPageFormOptions.patchValue(currentOptionsList);
    setTimeout(() => {
      this.updateImage = true;
    }, 10);
  }

  setPageDataHandler() {
    const data = {
      ...this.pageData,
      page: this.getData()
    };
    this.setPageData.emit(data);
  }

  getData() {
    const pageFormValues = this.selectPageForm.getRawValue();
    const options = this.selectPageFormOptions.getRawValue();
    map(options, (option, index) => {
      option.order = index;
    });
    return {
      ...pageFormValues,
      pageType: this.pageData.type,
      valid: this.selectPageForm.valid && this.selectPageFormOptions.valid,
      options
    };
  }
}
